import { useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography, alpha } from '@mui/material';
import TerminalIcon from '@mui/icons-material/Terminal';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { colors } from '../../theme';
import {
    fetchShellPassword,
    describeShellPasswordOutcome,
    type ShellPasswordOutcome,
} from './shellPassword';

/**
 * Reveal a node's escrowed shell password (#1542).
 *
 * The password is fetched only when the operator asks. Loading it with the page would put a
 * credential on screen for anyone walking past, and would file an audit row in
 * node_shell_password_lookups for a disclosure nobody requested - which makes that audit useless
 * for answering "who read this".
 */
export const ShellPasswordPanel = ({ nodeId }: { nodeId: string }) => {
    const [outcome, setOutcome] = useState<ShellPasswordOutcome | null>(null);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    const reveal = async () => {
        setLoading(true);
        setCopied(false);
        try {
            setOutcome(await fetchShellPassword(nodeId));
        } catch {
            setOutcome({ kind: 'error', status: 0 });
        } finally {
            setLoading(false);
        }
    };

    const hide = () => {
        setOutcome(null);
        setCopied(false);
    };

    const copy = async () => {
        if (outcome?.kind !== 'revealed') return;
        try {
            await navigator.clipboard.writeText(outcome.value.password);
            setCopied(true);
        } catch {
            // A clipboard the browser refuses is not worth an error banner: the value is on
            // screen and can be selected by hand.
            setCopied(false);
        }
    };

    const message = outcome ? describeShellPasswordOutcome(outcome) : '';
    const isProblem = outcome !== null && outcome.kind !== 'revealed';

    return (
        <Box sx={{ p: 2, border: `1px solid ${colors.slate[200]}`, borderRadius: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TerminalIcon sx={{ fontSize: 18, color: colors.slate[500] }} />
                <Typography sx={{ fontWeight: 600 }}>Shell password</Typography>
            </Box>

            <Typography sx={{ color: colors.slate[500], fontSize: 13, mb: 1.5 }}>
                The password this node set for its own <code>pi</code> account. Every lookup is recorded.
            </Typography>

            {outcome?.kind === 'revealed' ? (
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <code
                            data-testid="shell-password-value"
                            style={{
                                fontFamily: '"JetBrains Mono", monospace',
                                background: colors.slate[100],
                                padding: '8px 12px',
                                borderRadius: '6px',
                                flex: 1,
                            }}
                        >
                            {outcome.value.password}
                        </code>
                        <Tooltip title={copied ? 'Copied' : 'Copy'}>
                            <IconButton onClick={copy} aria-label="Copy shell password" size="small">
                                <ContentCopyIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Hide">
                            <IconButton onClick={hide} aria-label="Hide shell password" size="small">
                                <VisibilityOffIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Typography sx={{ color: colors.slate[400], fontSize: 12, mt: 1 }}>
                        Version {outcome.value.version}, escrowed {new Date(outcome.value.escrowedAt).toLocaleString()}
                    </Typography>
                </Box>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={reveal}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={14} /> : undefined}
                >
                    {loading ? 'Looking up' : 'Reveal password'}
                </Button>
            )}

            {isProblem && (
                <Typography
                    role="alert"
                    sx={{
                        mt: 1.5,
                        fontSize: 13,
                        // Undecryptable is a server fault the technician must escalate, not a
                        // miss they should re-check. It reads differently on purpose.
                        color: outcome.kind === 'none' ? colors.slate[500] : colors.error,
                        bgcolor: outcome.kind === 'none' ? 'transparent' : alpha(colors.error, 0.08),
                        p: outcome.kind === 'none' ? 0 : 1.5,
                        borderRadius: '8px',
                    }}
                >
                    {message}
                </Typography>
            )}
        </Box>
    );
};
